import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const FAQ = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Questions', icon: 'fas fa-th-list' },
    { id: 'general', name: 'General', icon: 'fas fa-info-circle' },
    { id: 'account', name: 'Account', icon: 'fas fa-user-shield' },
    { id: 'projects', name: 'Projects', icon: 'fas fa-project-diagram' },
    { id: 'technical', name: 'Technical', icon: 'fas fa-code' }
  ];

  const faqs = [
    {
      id: 1,
      question: 'What is DevRoom?',
      answer: 'DevRoom is a collaborative development platform designed to help developers build, manage, and scale projects efficiently. Our platform provides powerful tools for seamless collaboration, instant project setup, and scalable development workflows.',
      icon: 'fas fa-info-circle',
      category: 'general'
    },
    {
      id: 2,
      question: 'How do I get started with DevRoom?',
      answer: 'Getting started is easy! Simply register for an account, verify your email, and you can immediately begin creating projects and collaborating with your team.',
      icon: 'fas fa-rocket',
      category: 'general'
    },
    {
      id: 3,
      question: 'How do I reset my password?',
      answer: 'Go to your profile page and click on the "Change Password" button to securely update your password. You can also use the "Forgot Password" link on the login page.',
      icon: 'fas fa-lock',
      category: 'account'
    },
    {
      id: 4,
      question: 'How can I contact support?',
      answer: 'You can reach out to us at support@devroom.in for any support or queries. Our team is dedicated to helping you succeed with DevRoom.',
      icon: 'fas fa-envelope',
      category: 'general'
    },
    {
      id: 5,
      question: 'Can I collaborate with my team?',
      answer: 'Absolutely! DevRoom is built for collaboration. You can invite team members to projects, manage permissions, and work together in real-time.',
      icon: 'fas fa-users',
      category: 'projects'
    },
    {
      id: 6,
      question: 'What technologies does DevRoom support?',
      answer: 'DevRoom supports a wide range of technologies including Node.js, Python,and many more. Our platform is designed to be flexible and adaptable.',
      icon: 'fas fa-code',
      category: 'technical'
    },
    {
      id: 7,
      question: 'How do I manage project settings?',
      answer: 'Project settings can be managed from your project dashboard. You can update project details, manage collaborators, and configure security settings.',
      icon: 'fas fa-cog',
      category: 'projects'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="faq-page">
      <div className="faq-container">
        {/* Hero Section */}
        <div className="faq-hero">
          <div className="faq-hero-background">
            <div className="hero-gradient-orb"></div>
            <div className="hero-floating-particles">
              <div className="particle"></div>
              <div className="particle"></div>
              <div className="particle"></div>
            </div>
          </div>
          
          <div className="faq-hero-content">
            <div className="faq-badge">
              <i className="fas fa-question-circle"></i>
              <span>Help Center</span>
            </div>
            <h1 className="faq-title">Frequently Asked Questions</h1>
            <p className="faq-subtitle">
              Find answers to common questions about DevRoom
            </p>
            
            <div className="faq-search">
              <div className="search-input-container">
                <i className="fas fa-search search-icon"></i>
                <input 
                  type="text" 
                  placeholder="Search for answers..." 
                  className="search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button 
                    className="search-clear-btn" 
                    onClick={() => setSearchTerm('')}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Category Navigation */}
        <div className="faq-categories">
          <div className="categories-grid">
            {categories.map(category => (
              <button 
                key={category.id}
                className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                <i className={category.icon}></i>
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="faq-section">
          <div className="faq-list">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, idx) => (
                <div key={faq.id} className={`faq-item hover-lift ${openIndex === idx ? 'active' : ''}`}>
                  <div className="faq-header" onClick={() => toggleFAQ(idx)}>
                    <div className="faq-question-content">
                      <div className="faq-icon">
                        <i className={faq.icon}></i>
                      </div>
                      <h3 className="faq-question">{faq.question}</h3>
                    </div>
                    <div className="faq-toggle">
                      <i className={`fas fa-chevron-${openIndex === idx ? 'up' : 'down'}`}></i>
                    </div>
                  </div>
                  <div className="faq-answer-container">
                    <p className="faq-answer">{faq.answer}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">
                <i className="fas fa-search-minus"></i>
                <p>No results found for "{searchTerm}"</p>
                <button 
                  className="reset-search-btn" 
                  onClick={() => {
                    setSearchTerm('');
                    setActiveCategory('all');
                  }}
                >
                  Reset Search
                </button>
              </div>
            )}
          </div>
        </div>



        {/* Contact CTA Section */}
        <div className="faq-cta">
          <div className="faq-cta-content">
            <div className="cta-icon pulse-animation" style={{ color: '#0f3460' }}>
              <i className="fas fa-headset"></i>
            </div>
            <h2 className="cta-title">Still have questions?</h2>
            <p className="cta-description">
              Can't find what you're looking for? Get in touch with our support team.
            </p>
            <div className="cta-actions">
              <a 
                href="mailto:support@devroom.in"
                className="cta-btn primary"
                style={{ background: '#0f3460', boxShadow: '0 4px 15px rgba(0, 102, 255, 0.3)' }}
              >
                <i className="fas fa-envelope"></i>
                <span>Contact Support</span>
              </a>
              <button 
                onClick={() => navigate('/dashboard')}
                className="cta-btn secondary"
              >
                <i className="fas fa-arrow-left"></i>
                <span>Back to Dashboard</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
